import { useMemo, useRef, useState } from 'react';
import {
  UMAppShell, UMIcon, UMMap, UMMapControls, UMMapToolbar, UMR, UM_RAIL_ITEMS, type UMMapHandle,
} from '../../urbanmap/_components';
import { planLayers, planSources, DEFAULT_VISIBILITY } from './mapLayers';
import { PLAN } from './tokens';
import type { PlanBootstrap } from './types';
import { PlanLogo, fmt } from './ui';

const EMPTY = { modified: {}, drift: new Set<string>(), connected: new Set<string>() };

/**
 * Step 0 — the user is in UrbanMap (live data). A folder is selected in the toolbar,
 * and Plan is offered as the natural next step: "Créer un projet Plan avec ce périmètre".
 */
export function UrbanMapEntry({ data, onCreate, onOpenExisting }: { data: PlanBootstrap; onCreate: () => void; onOpenExisting: () => void }) {
  const mapRef = useRef<UMMapHandle>(null);
  const [search, setSearch] = useState('');
  const [folder, setFolder] = useState(data.umSelections[0]);
  const sources = useMemo(() => planSources(data.geo, EMPTY), [data.geo]);
  const layers = useMemo(
    () => planLayers({ ...DEFAULT_VISIBILITY, roads: false, zone: false }, { pickedId: null, scenarioShown: false, perimeterOnly: false }),
    [],
  );
  const inside = data.geo.buildings.features.filter((f) => f.properties?.inside === 1).length;

  return (
    <UMAppShell
      title="Plan — Proposition C"
      withStudioLayout={false}
      railItems={UM_RAIL_ITEMS.map((it) => (it.id === 'selection' ? { ...it, disabled: false } : it))}
      toolbar={
        <UMMapToolbar
          search={search}
          onSearchChange={setSearch}
          selections={data.umSelections}
          selectionLabel={folder}
          onSelectionChange={setFolder}
          onCenterSelection={() => mapRef.current?.fitBounds(data.geo.bounds)}
          onSelectionActions={onCreate}
          extra={
            <button
              type="button"
              onClick={onCreate}
              style={{
                pointerEvents: 'auto', height: 49, display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px', border: 'none',
                borderLeft: `1px solid ${UMR.border}`, background: PLAN.accent, color: UMR.white, fontFamily: UMR.font, fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              <UMIcon name="conversion_path" size={20} color={UMR.white} />
              Créer un projet Plan
            </button>
          }
        />
      }
    >
      <UMMap ref={mapRef} sources={sources} layers={layers} initialBounds={data.geo.bounds} />
      <UMMapControls basemap="light" availableBasemaps={['light']} onZoomIn={() => mapRef.current?.zoomIn()} onZoomOut={() => mapRef.current?.zoomOut()} />

      <aside
        aria-label="Découvrir Plan"
        style={{
          position: 'absolute', top: 64, right: 64, width: 340, zIndex: 20, background: UMR.white, fontFamily: UMR.font,
          borderTop: `4px solid ${PLAN.accent}`, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <PlanLogo size={36} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Passer au plan de développement</div>
            <div style={{ fontSize: 12, color: UMR.textSecondary }}>Sélection « {folder} » · {fmt(inside)} bâtiments</div>
          </div>
        </div>
        <p style={{ margin: 0, fontSize: 14, lineHeight: '21px' }}>
          Plan crée une <strong>copie de travail</strong> de ce périmètre : bâtiments, voirie et réseaux deviennent modifiables
          sans toucher à UrbanMap. Vous y testez ensuite des scénarios de développement.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={onCreate}
            style={{ flex: 1, height: 36, border: 'none', background: PLAN.accent, color: UMR.white, fontFamily: UMR.font, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
          >
            Créer un projet Plan
          </button>
          <button
            type="button"
            onClick={onOpenExisting}
            style={{ height: 36, padding: '0 12px', border: `1px solid ${PLAN.accent}`, background: UMR.white, color: PLAN.accentStrong, fontFamily: UMR.font, fontSize: 14, cursor: 'pointer' }}
          >
            Projets existants
          </button>
        </div>
      </aside>
    </UMAppShell>
  );
}
